import { HomeOutlined } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { selectImpactCalculateTourForCurrentProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { copyTourTravel } from '@/domain/project-tour-ontour/actions/copy-tour-travel.action';
import { deleteTourEvent } from '@/domain/project-tour-ontour/actions/delete-tour-event.action';
import { deleteTourTravel } from '@/domain/project-tour-ontour/actions/delete-tour-travel.action';
import { prepareAndAddTourTravel } from '@/domain/project-tour-ontour/actions/prepare-and-add-tour-travel.action';
import { TourEvent } from '@/domain/project-tour-ontour/entities/project-tour-ontour-event.entity';
import {
  TourTravel,
  TourTravelConnexionTypeWithEvent,
} from '@/domain/project-tour-ontour/entities/project-tour-ontour-travel.entity';
import {
  selectProjectTourOnTourEvents,
  selectProjectTourOnTourIsFetchingTourEvents,
  selectProjectTourOnTourIsFetchingTourTravels,
  selectProjectTourOnTourTravels,
} from '@/domain/project-tour-ontour/project-tour-ontour.selectors';
import { getTourTravelsLinkedToTourEvent } from '@/domain/project-tour-ontour/utils/get-tour-travels-linked-to-tour-event';
import { useDialogConfirm } from '@/ui/components/confirm-dialog/useDialogConfirm';
import { ScoreText } from '@/ui/components/score-text/ScoreText';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatCo2Value } from '@/ui/utils/format-co2-value';

import { TourCard } from '../components/TourCard';
import { AddTourEventsButtonAndModal } from './components/AddTourEventsButtonAndModal';
import { TourTraveListIncoming } from './components/tour-travel-list/TourTraveListIncoming';
import { TourTraveListDirect } from './components/tour-travel-list/TourTraveListlDirect';
import { TourTraveListOutcoming } from './components/tour-travel-list/TourTraveListOutcoming';
import { TourEventItem } from './components/TourEventItem';
import { TourTravelAddButton } from './components/TourTravelAddButton';
import { TourTraveListBodyEmpty } from './components/TourTraveListBodyEmpty';
import { TourTraveListBodyLoading } from './components/TourTraveListBodyLoading';
import { prepareTourTravelsToDisplayList } from './utils/prepare-tour-travels-for-render';
import {
  buildFavoriteGroupKey,
  buildFavoriteGroupOptions,
} from './utils/tour-travel-favorite-group';

export function TourTravelsAndDates({ sequenceId }: { sequenceId: number }) {
  const dispatch = useAppDispatch();
  const dialogConfirm = useDialogConfirm();
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const [favoriteGroupKeys, setFavoriteGroupKeys] = useState<Set<string>>(
    new Set(),
  );

  const onToggleFavorite = (id: number) => {
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onToggleFavoriteGroup = (key: string) => {
    setFavoriteGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const tourEvents = useAppSelector(selectProjectTourOnTourEvents);
  const tourTravels = useAppSelector(selectProjectTourOnTourTravels);
  const isFetchingTourEvents = useAppSelector(
    selectProjectTourOnTourIsFetchingTourEvents,
  );
  const isFetchingTourTravels = useAppSelector(
    selectProjectTourOnTourIsFetchingTourTravels,
  );

  const calculateTour = useAppSelector(
    selectImpactCalculateTourForCurrentProject,
  );
  const co2TourEventForSequence =
    calculateTour?.co2?.ontour?.bySequence[sequenceId]?.events || 0;

  const co2AllTravels =
    calculateTour?.co2?.ontour?.bySequence[sequenceId]?.travels || 0;

  const onClickAddTourTravel = (
    connexionType: TourTravelConnexionTypeWithEvent,
    fromTourEventId: number,
    toTourEventId: number,
  ) => {
    dispatch(
      prepareAndAddTourTravel({
        sequenceId,
        connexionType,
        fromTourEventId,
        toTourEventId,
      }),
    );
  };

  const onClickDeleteTourEvent = async (tourEventId: number) => {
    const tourTravelsLinked = getTourTravelsLinkedToTourEvent(
      tourTravels,
      tourEventId,
    );
    // tour travels are linked => confirm before delete
    if (tourTravelsLinked.length > 0) {
      const count = tourTravelsLinked.length;
      const response = await dialogConfirm({
        title: (
          <>
            {translate('DeleteTourTravel.Message.confirmation.part1', {
              count,
            })}
            <br />
            {translate('DeleteTourTravel.Message.confirmation.part2', {
              count,
            })}
          </>
        ),
      });
      if (response !== 'confirmed') {
        return;
      }
    }
    dispatch(deleteTourEvent({ tourEventId, sequenceId }));
  };

  const onClickDeleteTourTravel = (tourTravelId: number) => {
    dispatch(deleteTourTravel({ tourTravelId, sequenceId }));
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      next.delete(tourTravelId);
      return next;
    });
  };

  const onCopyFromFavoriteTourTravel = (
    sourceTourTravel: TourTravel,
    fromTourEventId: number | undefined,
    toTourEventId: number | undefined,
  ) => {
    dispatch(
      copyTourTravel({
        sourceTourTravel,
        sequenceId,
        fromTourEventId,
        toTourEventId,
      }),
    );
  };
  return (
    <TourCard
      title={translate('ProjectsTour.OnTour.travels-and-dates')}
      headerRightComponent={
        <Stack sx={{ flexDirection: 'row', gap: 2 }}>
          <ScoreText
            value={`${formatCo2Value(co2TourEventForSequence)} ${translate('Projects.teCO2')}`}
            text={translate('ProjectsTour.dates')}
            variant={'box'}
          />
          <ScoreText
            value={`${formatCo2Value(co2AllTravels)} ${translate('Projects.teCO2')}`}
            text={translate('ProjectsTour.travels')}
            variant={'box'}
          />
        </Stack>
      }
    >
      <Stack
        sx={{
          width: '100%',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ alignSelf: 'flex-end' }}>
          {tourEvents.length > 0 && (
            <AddTourEventsButtonAndModal sequenceId={sequenceId} />
          )}
        </Box>
        <TourEventListStart />
        <TourEventListBody
          tourEvents={tourEvents}
          tourTravels={tourTravels}
          sequenceId={sequenceId}
          isFetchingTourEvents={isFetchingTourEvents}
          isFetchingTourTravels={isFetchingTourTravels}
          onClickAddTourTravel={onClickAddTourTravel}
          onClickDeleteTourEvent={onClickDeleteTourEvent}
          onClickDeleteTourTravel={onClickDeleteTourTravel}
          favoriteIds={favoriteIds}
          onToggleFavorite={onToggleFavorite}
          favoriteGroupKeys={favoriteGroupKeys}
          onToggleFavoriteGroup={onToggleFavoriteGroup}
          onCopyFromFavoriteTourTravel={onCopyFromFavoriteTourTravel}
        />
        <TourEventListEnd />
      </Stack>
    </TourCard>
  );
}

function TourEventListBody({
  tourEvents,
  tourTravels,
  sequenceId,
  isFetchingTourEvents,
  isFetchingTourTravels,
  onClickAddTourTravel,
  onClickDeleteTourEvent,
  onClickDeleteTourTravel,
  favoriteIds,
  onToggleFavorite,
  favoriteGroupKeys,
  onToggleFavoriteGroup,
  onCopyFromFavoriteTourTravel,
}: {
  tourEvents: TourEvent[];
  tourTravels: TourTravel[];
  sequenceId: number;
  isFetchingTourEvents: boolean;
  isFetchingTourTravels: boolean;
  onClickAddTourTravel: (
    connexionType: TourTravelConnexionTypeWithEvent,
    fromTourEventId: number,
    toTourEventId: number,
  ) => void;
  onClickDeleteTourEvent: (tourEventId: number) => void;
  onClickDeleteTourTravel: (tourTravelId: number) => void;
  favoriteIds: Set<number>;
  onToggleFavorite: (id: number) => void;
  favoriteGroupKeys: Set<string>;
  onToggleFavoriteGroup: (key: string) => void;
  onCopyFromFavoriteTourTravel: (
    sourceTourTravel: TourTravel,
    fromTourEventId: number | undefined,
    toTourEventId: number | undefined,
  ) => void;
}) {
  const tourTravelsByTourEventId = prepareTourTravelsToDisplayList(
    tourEvents,
    tourTravels,
  );

  const onClickAddTourTravelHandler = (
    connexionType: TourTravelConnexionTypeWithEvent,
    index: number,
  ) => {
    let fromTourEventId = NaN;
    let toTourEventId = NaN;
    switch (connexionType) {
      case 'incoming':
        toTourEventId = tourEvents[index].id;
        break;
      case 'outgoing':
        fromTourEventId = tourEvents[index].id;
        break;
      case 'direct':
        fromTourEventId = tourEvents[index].id;
        toTourEventId = tourEvents[index + 1].id;
        break;
    }
    onClickAddTourTravel(connexionType, fromTourEventId, toTourEventId);
  };

  const buildCopyHandler = (
    connexionType: TourTravelConnexionTypeWithEvent,
    index: number,
  ) => {
    let fromTourEventId: number | undefined;
    let toTourEventId: number | undefined;
    switch (connexionType) {
      case 'incoming':
        toTourEventId = tourEvents[index]?.id;
        break;
      case 'outgoing':
        fromTourEventId = tourEvents[index]?.id;
        break;
      case 'direct':
        fromTourEventId = tourEvents[index]?.id;
        toTourEventId = tourEvents[index + 1]?.id;
        break;
    }
    return (sourceTourTravel: TourTravel) =>
      onCopyFromFavoriteTourTravel(
        sourceTourTravel,
        fromTourEventId,
        toTourEventId,
      );
  };

  const isLoading =
    (isFetchingTourEvents || isFetchingTourTravels) && tourEvents.length === 0;
  if (isLoading) {
    return <TourTraveListBodyLoading />;
  }

  if (tourEvents.length === 0) {
    return <TourTraveListBodyEmpty sequenceId={sequenceId} />;
  }

  return (
    <Stack sx={{ flexDirection: 'column', alignItems: 'center' }}>
      {tourEvents.map((tourEvent, index) => {
        const isLastTourEvent = index < tourEvents.length - 1;
        return (
          <Box key={index}>
            <Stack sx={{ flexDirection: 'column', alignItems: 'center' }}>
              {/* tour-event : line 1/2  */}
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                {/* left side : incoming */}
                <TourTravelsLinkedView
                  tourTravels={tourTravelsByTourEventId[tourEvent.id]}
                  allTourTravels={tourTravels}
                  tourEvents={tourEvents}
                  anchorEventId={tourEvent.id}
                  connexionTypeToShow={'incoming'}
                  isLastTourEvent={isLastTourEvent}
                  onClickDelete={onClickDeleteTourTravel}
                  onClickAddTourTravel={() =>
                    onClickAddTourTravelHandler('incoming', index)
                  }
                  favoriteIds={favoriteIds}
                  onToggleFavorite={onToggleFavorite}
                  favoriteGroupKeys={favoriteGroupKeys}
                  onToggleFavoriteGroup={onToggleFavoriteGroup}
                  onCopyTravel={buildCopyHandler('incoming', index)}
                />
                {/*  tour event */}
                <TourEventItem
                  tourEvent={tourEvent}
                  onClickDelete={onClickDeleteTourEvent}
                />
                {/* right side : outgoing */}
                <TourTravelsLinkedView
                  tourTravels={tourTravelsByTourEventId[tourEvent.id]}
                  allTourTravels={tourTravels}
                  tourEvents={tourEvents}
                  anchorEventId={tourEvent.id}
                  connexionTypeToShow={'outgoing'}
                  isLastTourEvent={isLastTourEvent}
                  onClickDelete={onClickDeleteTourTravel}
                  onClickAddTourTravel={() =>
                    onClickAddTourTravelHandler('outgoing', index)
                  }
                  favoriteIds={favoriteIds}
                  onToggleFavorite={onToggleFavorite}
                  favoriteGroupKeys={favoriteGroupKeys}
                  onToggleFavoriteGroup={onToggleFavoriteGroup}
                  onCopyTravel={buildCopyHandler('outgoing', index)}
                />
              </Stack>
              {/* tour-event : line 2/2  */}
              {/* down side : direct */}
              <TourTravelsLinkedView
                tourTravels={tourTravelsByTourEventId[tourEvent.id]}
                allTourTravels={tourTravels}
                tourEvents={tourEvents}
                anchorEventId={tourEvent.id}
                connexionTypeToShow={'direct'}
                isLastTourEvent={isLastTourEvent}
                onClickDelete={onClickDeleteTourTravel}
                onClickAddTourTravel={() =>
                  onClickAddTourTravelHandler('direct', index)
                }
                favoriteIds={favoriteIds}
                onToggleFavorite={onToggleFavorite}
                favoriteGroupKeys={favoriteGroupKeys}
                onToggleFavoriteGroup={onToggleFavoriteGroup}
                onCopyTravel={buildCopyHandler('direct', index)}
              />
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function TourTravelsLinkedView({
  tourTravels,
  allTourTravels,
  tourEvents,
  anchorEventId,
  connexionTypeToShow,
  isLastTourEvent,
  onClickAddTourTravel,
  onClickDelete,
  favoriteIds,
  onToggleFavorite,
  favoriteGroupKeys,
  onToggleFavoriteGroup,
  onCopyTravel,
}: {
  tourTravels: TourTravel[];
  allTourTravels: TourTravel[];
  tourEvents: TourEvent[];
  anchorEventId: number;
  connexionTypeToShow: TourTravelConnexionTypeWithEvent;
  isLastTourEvent: boolean;
  onClickAddTourTravel: () => void;
  onClickDelete: (tourTravelId: number) => void;
  favoriteIds: Set<number>;
  onToggleFavorite: (id: number) => void;
  favoriteGroupKeys: Set<string>;
  onToggleFavoriteGroup: (key: string) => void;
  onCopyTravel: (sourceTourTravel: TourTravel) => void;
}) {
  const tourTravelsToShow = tourTravels.filter(
    (x) => x.connexionTypeWithEvent === connexionTypeToShow,
  );
  const tourTravelListIsEmpty = tourTravelsToShow.length === 0;
  const favoriteTravels = allTourTravels.filter((x) => {
    if (!favoriteIds.has(x.id)) return false;
    if (connexionTypeToShow === 'direct')
      return x.connexionTypeWithEvent === 'direct';
    return (
      x.connexionTypeWithEvent === 'incoming' ||
      x.connexionTypeWithEvent === 'outgoing'
    );
  });

  const windowGroupKey = buildFavoriteGroupKey(
    connexionTypeToShow,
    anchorEventId,
  );
  const isWindowFavorite = favoriteGroupKeys.has(windowGroupKey);
  const onToggleWindowFavorite = () => onToggleFavoriteGroup(windowGroupKey);
  const favoriteGroups = buildFavoriteGroupOptions({
    favoriteGroupKeys,
    allTravels: allTourTravels,
    tourEvents,
    targetConnexionType: connexionTypeToShow,
    excludeKey: windowGroupKey,
  });
  const onCopyGroup = (travels: TourTravel[]) => {
    travels.forEach((travel) => onCopyTravel(travel));
  };
  switch (connexionTypeToShow) {
    case 'incoming':
      return (
        <Stack sx={{ flexDirection: 'column', alignItems: 'end' }}>
          <TourTraveListIncoming
            tourTravels={tourTravelsToShow}
            onClickDelete={onClickDelete}
            onClickAdd={onClickAddTourTravel}
            favoriteIds={favoriteIds}
            onToggleFavorite={onToggleFavorite}
            favoriteTravels={favoriteTravels}
            onCopyTravel={onCopyTravel}
            isWindowFavorite={isWindowFavorite}
            onToggleWindowFavorite={onToggleWindowFavorite}
            favoriteGroups={favoriteGroups}
            onCopyGroup={onCopyGroup}
          />
          <TourTravelAddButton
            connexionType={'incoming'}
            tourTravelListIsEmpty={tourTravelListIsEmpty}
            onClick={onClickAddTourTravel}
          />
        </Stack>
      );
    case 'outgoing':
      return (
        <Stack sx={{ flexDirection: 'column', alignItems: 'start' }}>
          <TourTraveListOutcoming
            tourTravels={tourTravelsToShow}
            onClickDelete={onClickDelete}
            onClickAdd={onClickAddTourTravel}
            favoriteIds={favoriteIds}
            onToggleFavorite={onToggleFavorite}
            favoriteTravels={favoriteTravels}
            onCopyTravel={onCopyTravel}
            isWindowFavorite={isWindowFavorite}
            onToggleWindowFavorite={onToggleWindowFavorite}
            favoriteGroups={favoriteGroups}
            onCopyGroup={onCopyGroup}
          />
          <TourTravelAddButton
            connexionType={'outgoing'}
            tourTravelListIsEmpty={tourTravelListIsEmpty}
            onClick={onClickAddTourTravel}
          />
        </Stack>
      );
    case 'direct':
      return (
        <Stack
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: isLastTourEvent ? 50 : 0,
          }}
        >
          <TourTraveListDirect
            tourTravels={tourTravelsToShow}
            onClickDelete={onClickDelete}
            onClickAdd={onClickAddTourTravel}
            favoriteIds={favoriteIds}
            onToggleFavorite={onToggleFavorite}
            favoriteTravels={favoriteTravels}
            onCopyTravel={onCopyTravel}
            isWindowFavorite={isWindowFavorite}
            onToggleWindowFavorite={onToggleWindowFavorite}
            favoriteGroups={favoriteGroups}
            onCopyGroup={onCopyGroup}
          />
          {isLastTourEvent && (
            <TourTravelAddButton
              connexionType={'direct'}
              tourTravelListIsEmpty={tourTravelListIsEmpty}
              onClick={onClickAddTourTravel}
            />
          )}
        </Stack>
      );
  }
}

function TourEventListStart() {
  return (
    <Stack sx={{ flexDirection: 'row', gap: 2 }}>
      <HomeOutlined />
      <Typography>Départ</Typography>
    </Stack>
  );
}

function TourEventListEnd() {
  return (
    <Stack sx={{ flexDirection: 'row', gap: 2 }}>
      <HomeOutlined />
      <Typography>Arrivée</Typography>
    </Stack>
  );
}
